import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { Tabs, XYScatterPlot } from "@jana-technology/platform-ui";

import {
  RiskManagementMajorEventComparisonChartDocument,
  RiskManagementOverallComparisonChartDocument,
} from "$/__generated__/graphql";
import { useGlobalFiltersContext } from "contexts/filters";
import { useBaseBarChartProps } from "hooks/useBaseBarChartProps";

import ChartCard from "./ChartCard";
import type {
  RiskComparisonCardHeadingProps,
  RiskComparisonChartConfig,
  RiskComparisonPeerWells,
} from "./types";
import {
  buildMitigationRiskComparisonChartConfig,
  buildRiskComparisonChartConfig,
} from "./helpers";

export const RiskComparisonCardHeading = ({
  onValueChange,
}: RiskComparisonCardHeadingProps) => (
  <div className="flex items-baseline justify-between">
    <span>Risk Comparison</span>
    <Tabs
      kind="secondary"
      className="font-normal"
      items={[
        {
          label: "Operational Risk",
          id: "risk-comparison-or",
        },
        {
          label: "Intervention Risk",
          id: "risk-comparison-ir",
        },
      ]}
      onValueChange={onValueChange}
    />
  </div>
);

export const OverallRiskComparison = () => {
  const {
    appliedWell,
    appliedRiskMitigationId,
    wellNamesByHierarchyQueryResult,
    assetSelectionQueryResult,
  } = useGlobalFiltersContext();

  const [hierarchyId, wellName] = appliedWell;

  const [isBaseline, setIsBaseline] = useState(true);

  const onValueChange = useCallback((value: string) => {
    if (value === "risk-comparison-or") {
      setIsBaseline(true);
    } else {
      setIsBaseline(false);
    }
  }, []);

  const overallRiskComparisonResult = useQuery(
    RiskManagementOverallComparisonChartDocument,
    {
      skip: !wellName.length,
      variables: {
        hierarchyId,
        wellName,
        isBaseline,
        riskMitigationTypeId: appliedRiskMitigationId,
      },
    },
  );

  const { series, referenceLines, legendDomain, theme, legendColors } =
    useMemo<RiskComparisonChartConfig>(() => {
      const wellNamesByHierarchy: RiskComparisonPeerWells | undefined =
        wellNamesByHierarchyQueryResult.data?.wellNamesGroupedByHierarchy?.map(
          (item) => ({
            hierarchyId: item?.hierarchyId ?? null,
            wellNames: item?.wellNames ?? [],
          }),
        );

      return buildRiskComparisonChartConfig(
        {
          chartData:
            overallRiskComparisonResult.data
              ?.riskManagementOverallComparisonChart,
          wellName,
          hierarchyId,
          wellNamesByHierarchy,
          hierarchies: assetSelectionQueryResult.data?.hierarchies,
        },
        isBaseline,
        true,
      );
    }, [
      hierarchyId,
      overallRiskComparisonResult.data,
      wellName,
      wellNamesByHierarchyQueryResult.data,
      isBaseline,
      assetSelectionQueryResult.data?.hierarchies,
    ]);

  // this chart doesn't read yAxisLabel from a route handle; isBaseline maps to OR|IR
  const { height, width } = useBaseBarChartProps();

  return (
    <ChartCard
      heading={<RiskComparisonCardHeading onValueChange={onValueChange} />}
      loading={overallRiskComparisonResult.loading}
    >
      <XYScatterPlot
        width={width}
        height={height}
        series={series}
        xAxisLabel="Nth-Ranked Well"
        yAxisLabel={`${isBaseline ? "Operational Risk" : "Intervention Risk"}`}
        theme={theme}
        referenceLines={referenceLines}
        legendDomain={legendDomain}
        legendRange={legendColors}
        yDomainPadding={1.05}
        yScale={{
          type: "log",
        }}
      />
    </ChartCard>
  );
};

export const MitigationRiskComparison = () => {
  const {
    appliedWell,
    appliedRiskMitigationId,
    wellNamesByHierarchyQueryResult,
    assetSelectionQueryResult,
    riskMitigationQueryResult,
  } = useGlobalFiltersContext();

  const [hierarchyId, wellName] = appliedWell;

  const { riskMitigations } = riskMitigationQueryResult.data ?? {};

  const riskMitigationName =
    riskMitigations?.find(
      (mitigation) => mitigation?.id === appliedRiskMitigationId,
    )?.value ?? "";

  const overallRiskComparisonResult = useQuery(
    RiskManagementOverallComparisonChartDocument,
    {
      skip: !wellName.length,
      variables: {
        hierarchyId,
        wellName,
        isBaseline: true,
        riskMitigationTypeId: appliedRiskMitigationId,
      },
    },
  );

  const { series, referenceLines, legendDomain, theme, legendColors } =
    useMemo<RiskComparisonChartConfig>(() => {
      const wellNamesByHierarchy: RiskComparisonPeerWells | undefined =
        wellNamesByHierarchyQueryResult.data?.wellNamesGroupedByHierarchy?.map(
          (item) => ({
            hierarchyId: item?.hierarchyId ?? null,
            wellNames: item?.wellNames ?? [],
          }),
        );

      return buildMitigationRiskComparisonChartConfig(
        {
          chartData:
            overallRiskComparisonResult.data
              ?.riskManagementOverallComparisonChart,
          wellName,
          hierarchyId,
          wellNamesByHierarchy,
          hierarchies: assetSelectionQueryResult.data?.hierarchies,
        },
        riskMitigationName,
      );
    }, [
      hierarchyId,
      overallRiskComparisonResult.data,
      wellName,
      wellNamesByHierarchyQueryResult.data,
      assetSelectionQueryResult.data?.hierarchies,
      riskMitigationName,
    ]);

  // this chart doesn't read yAxisLabel from a route handle; isBaseline maps to OR|IR
  const { height, width } = useBaseBarChartProps();

  return (
    <ChartCard
      heading="Risk Comparison"
      loading={overallRiskComparisonResult.loading}
    >
      <XYScatterPlot
        width={width}
        height={height}
        series={series}
        xAxisLabel="Nth-Ranked Well"
        yAxisLabel="Operational Risk"
        theme={theme}
        referenceLines={referenceLines}
        legendDomain={legendDomain}
        legendRange={legendColors}
        yDomainPadding={1.05}
        yScale={{
          type: "log",
        }}
      />
    </ChartCard>
  );
};

export const MajorEventRiskComparison = () => {
  const {
    appliedWell,
    wellNamesByHierarchyQueryResult,
    assetSelectionQueryResult,
  } = useGlobalFiltersContext();

  const [hierarchyId, wellName] = appliedWell;

  const [isBaseline, setIsBaseline] = useState(true);

  const onValueChange = useCallback((value: string) => {
    if (value === "risk-comparison-or") {
      setIsBaseline(true);
    } else {
      setIsBaseline(false);
    }
  }, []);

  const majorEventRiskComparisonResult = useQuery(
    RiskManagementMajorEventComparisonChartDocument,
    {
      skip: !wellName.length,
      variables: {
        hierarchyId,
        wellName,
        isBaseline,
      },
    },
  );

  const { series, referenceLines, legendDomain, theme, legendColors } =
    useMemo<RiskComparisonChartConfig>(() => {
      const wellNamesByHierarchy: RiskComparisonPeerWells | undefined =
        wellNamesByHierarchyQueryResult.data?.wellNamesGroupedByHierarchy?.map(
          (item) => ({
            hierarchyId: item?.hierarchyId ?? null,
            wellNames: item?.wellNames ?? [],
          }),
        );

      return buildRiskComparisonChartConfig(
        {
          chartData:
            majorEventRiskComparisonResult.data
              ?.riskManagementMajorEventComparisonChart,
          wellName,
          hierarchyId,
          wellNamesByHierarchy,
          hierarchies: assetSelectionQueryResult.data?.hierarchies,
        },
        isBaseline,
        false,
      );
    }, [
      isBaseline,
      hierarchyId,
      majorEventRiskComparisonResult.data,
      wellName,
      wellNamesByHierarchyQueryResult.data,
      assetSelectionQueryResult.data?.hierarchies,
    ]);

  // this chart doesn't read yAxisLabel from a route handle; isBaseline maps to OR|IR
  const { height, width } = useBaseBarChartProps();

  return (
    <ChartCard
      heading={<RiskComparisonCardHeading onValueChange={onValueChange} />}
      loading={majorEventRiskComparisonResult.loading}
    >
      <XYScatterPlot
        width={width}
        height={height}
        series={series}
        xAxisLabel="Nth-Ranked Well"
        yAxisLabel={`${isBaseline ? "Operational Risk" : "Intervention Risk"}`}
        theme={theme}
        referenceLines={referenceLines}
        legendDomain={legendDomain}
        legendRange={legendColors}
        yDomainPadding={1.05}
        yScale={{
          type: "log",
        }}
      />
    </ChartCard>
  );
};

export default MajorEventRiskComparison;
